import { memo } from "react";

import { useQuery } from "@tanstack/react-query";
import { Activity, ChevronDown } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { supabase } from "@/integrations/supabase/client";

interface MomentumStats {
  high: number;
  medium: number;
  low: number;
  total: number;
}

interface CompactMomentumIndicatorProps {
  workspaceId: string;
}

/**
 * CompactMomentumIndicator - A minimal, non-intrusive momentum display
 * 
 * Design principles:
 * - Shows only high-level summary in header
 * - Expandable dropdown for details
 * - Uses calm colors
 * - Optional - can be hidden if overwhelming
 */
const CompactMomentumIndicatorComponent = ({ workspaceId }: CompactMomentumIndicatorProps) => {
  const { data: stats = { high: 0, medium: 0, low: 0, total: 0 }, isLoading } = useQuery<MomentumStats>({
    queryKey: ['momentumStats', workspaceId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_workspace_momentum", {
        p_workspace_id: workspaceId,
      });

      if (error) throw error;

      const momentum = data || [];
      return {
        high: momentum.filter((p: any) => p.momentum_level === "high").length,
        medium: momentum.filter((p: any) => p.momentum_level === "medium").length,
        low: momentum.filter((p: any) => p.momentum_level === "low").length,
        total: momentum.length,
      };
    },
    enabled: !!workspaceId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  if (isLoading || stats.total === 0) {
    return null;
  }

  // Determine overall status with calming language
  const getOverallStatus = () => {
    if (stats.low === 0) return { text: "All good", variant: "default" as const };
    if (stats.low <= 2) return { text: "Mostly good", variant: "secondary" as const };
    return { text: "Some attention needed", variant: "outline" as const };
  };

  const status = getOverallStatus();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-2">
          <Activity className="h-4 w-4 text-muted-foreground" />
          <Badge variant={status.variant} className="font-normal">
            {status.text}
          </Badge>
          <ChevronDown className="h-3 w-3 text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="text-xs text-muted-foreground">
          Project Momentum ({stats.total} total)
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        
        {stats.high > 0 && (
          <DropdownMenuItem className="cursor-default">
            <div className="flex items-center justify-between w-full">
              <span className="text-sm">Active</span>
              <Badge variant="outline" className="text-xs bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800">
                {stats.high}
              </Badge>
            </div>
          </DropdownMenuItem>
        )}
        
        {stats.medium > 0 && (
          <DropdownMenuItem className="cursor-default">
            <div className="flex items-center justify-between w-full">
              <span className="text-sm">Moderate</span>
              <Badge variant="outline" className="text-xs bg-yellow-50 dark:bg-yellow-950 text-yellow-700 dark:text-yellow-300 border-yellow-200 dark:border-yellow-800">
                {stats.medium}
              </Badge>
            </div>
          </DropdownMenuItem>
        )}
        
        {stats.low > 0 && (
          <DropdownMenuItem className="cursor-default">
            <div className="flex items-center justify-between w-full">
              <span className="text-sm">Could use attention</span>
              <Badge variant="outline" className="text-xs bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800">
                {stats.low}
              </Badge>
            </div>
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const CompactMomentumIndicator = memo(CompactMomentumIndicatorComponent);
